import decodeObject from '@schibsted-nmp/search-frontend-utils/decodeObject.js';
import {
    QueryClient,
    QueryClientProvider,
    hydrate,
    type DehydratedState,
} from '@tanstack/react-query';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { App } from './App';
import type { AppProps } from './AppProps';
import { loadAndActivateI18n } from './i18n';
import { MetaTagsClient, MetaTagsClientProvider } from './meta/MetaTagsClient';
import { PodletsProvider, type EmbeddedPodlets } from './podlets/Podlets';

const containerEle = document.getElementById('app');
const propsEle = document.querySelector(
    `script[type="application/json"][data-props]`,
);
const reactQueryStateEle = document.querySelector(
    `script[type="application/json"][data-react-query-state]`,
);
const podletsEle = document.querySelector(
    `script[type="application/json"][data-podlets]`,
);

if (containerEle && propsEle && reactQueryStateEle && podletsEle) {
    try {
        const props = decodeObject<AppProps>(propsEle.textContent ?? '{}');
        const reactQueryState = decodeObject<DehydratedState>(
            reactQueryStateEle.textContent ?? '{}',
        );
        const podlets = decodeObject<EmbeddedPodlets>(
            podletsEle.textContent ?? '{}',
        );

        loadAndActivateI18n(props.locale);

        const metaTagsClient = new MetaTagsClient();

        hydrateRoot(
            containerEle,
            <MetaTagsClientProvider client={metaTagsClient}>
                <PodletsProvider podlets={podlets}>
                    <HydratedApp dehydratedState={reactQueryState} {...props}>
                        {
                            // @ts-expect-error This variable is defined by esbuild
                            // eslint-disable-next-line react/jsx-no-undef
                            <Page />
                        }
                    </HydratedApp>
                </PodletsProvider>
            </MetaTagsClientProvider>,
        );
    } catch (err) {
        throw new Error(
            `SSR(hydrate): Props (${
                propsEle.textContent
            }) were provided but parse failed: Error message was: ${
                (err as Error).message
            }`,
        );
    }
}

type HydratedAppProps = AppProps & {
    children?: React.ReactNode;
    dehydratedState: unknown;
};

function HydratedApp(props: HydratedAppProps) {
    const { children, dehydratedState, ...appProps } = props;

    const [queryClient] = React.useState(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                    },
                },
            }),
    );

    hydrate(queryClient, dehydratedState);

    return (
        <QueryClientProvider client={queryClient}>
            <App {...appProps}>{children}</App>
        </QueryClientProvider>
    );
}
